/**
 * Shared markdown component-overrides used by every article template.
 *
 * - `img` → wraps in `<figure class="article-figure">` with optional
 *   `data-ratio="NxM"` and `.article-figure--wide` driven by query-string
 *   conventions on the markdown image src.
 * - `blockquote` → adds `.article-pullquote` so markdown `>` quotes render
 *   with the lime left bar from article-styles.css.
 *
 * Pair with `remark-unwrap-images` in the remark plugins so standalone
 * images aren't wrapped in a `<p>` (which would make `<figure>` inside `<p>`
 * — invalid HTML).
 */
import type { Components } from "react-markdown";
import type { ReactNode } from "react";

function parseImageSrc(rawSrc: string): {
  src: string;
  ratio: string | null;
  wide: boolean;
} {
  if (!rawSrc) return { src: rawSrc, ratio: null, wide: false };
  const qIdx = rawSrc.indexOf("?");
  if (qIdx < 0) return { src: rawSrc, ratio: null, wide: false };
  const base = rawSrc.slice(0, qIdx);
  const query = rawSrc.slice(qIdx + 1);
  let ratio: string | null = null;
  let wide = false;
  for (const pair of query.split("&")) {
    if (!pair) continue;
    const [k, v] = pair.includes("=") ? pair.split("=", 2) : [pair, ""];
    if (k === "ratio" && /^\d+x\d+$/.test(v)) ratio = v;
    else if (k === "wide") wide = true;
  }
  return { src: base, ratio, wide };
}

/**
 * Build a Components map honouring the article spec. `pTransform` lets the
 * caller decide whether each paragraph should carry the .article-lede-p
 * drop-cap class (templates split body into chapters/points and own that
 * decision themselves).
 */
export function articleMarkdownComponents(opts: {
  pTransform?: (children: ReactNode) => ReactNode;
} = {}): Components {
  return {
    p({ children }: { children?: ReactNode }) {
      if (opts.pTransform) return opts.pTransform(children) as ReactNode;
      return <p>{children}</p>;
    },
    img({ src, alt }: { src?: string | Blob; alt?: string }) {
      const rawSrc = typeof src === "string" ? src : "";
      const { src: cleanSrc, ratio, wide } = parseImageSrc(rawSrc);
      if (!cleanSrc) return null;
      const figClass = wide ? "article-figure article-figure--wide" : "article-figure";
      const caption = (alt ?? "").trim();
      return (
        <figure className={figClass} data-ratio={ratio ?? undefined}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={cleanSrc} alt={caption} />
          {caption ? <figcaption>{caption}</figcaption> : null}
        </figure>
      );
    },
    blockquote({ children }: { children?: ReactNode }) {
      return <blockquote className="article-pullquote">{children}</blockquote>;
    },
  };
}
